import React, { useState, createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CollapsibleTable from '../CollapsibleTable';
import 'github-markdown-css/github-markdown.css';

// Context to track list type for proper rendering
const ListContext = createContext<'ul' | 'ol' | null>(null);

interface MarkdownRendererProps {
  content: string;
  className?: string;
  variant?: 'default' | 'compact' | 'card' | 'chat' | 'tiny';
  bordered?: boolean;
  images?: string[]; // Array of image URLs to be used in tables
  isExporting?: boolean;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className = '',
  variant = 'default',
  bordered = false,
  images = [],
  isExporting = false
}) => {
  // Safety check: ensure content is a string
  if (!content || typeof content !== 'string') {
    return (
      <div className={className}>
        <p className="text-gray-500 text-sm">No content available</p>
      </div>
    );
  }

  // State for managing expanded lists
  const [expandedLists, setExpandedLists] = useState<Record<string, boolean>>({});

  // Toggle list expansion
  const toggleList = (listId: string) => {
    setExpandedLists(prev => {
      const newState = {
        ...prev,
        [listId]: !prev[listId]
      };
      return newState;
    });
  };

  // Define styling variants based on BluePill AI brand system
  const getVariantStyles = () => {
    switch (variant) {
      case 'tiny':
        return {
          h1: "text-xs font-semibold text-text-primary mt-2 mb-1 first:mt-0",
          h2: "text-xs font-semibold text-text-primary mt-1.5 mb-1 first:mt-0",
          h3: "text-xs font-medium text-text-primary mt-1 mb-0.5 first:mt-0",
          p: "text-xs text-gray-500 mb-1 leading-relaxed",
          spacing: "space-y-0.5"
        };
      case 'compact':
        return {
          h1: "text-lg font-semibold text-text-primary mt-4 mb-2 first:mt-0",
          h2: "text-base font-semibold text-text-primary mt-3 mb-2 first:mt-0",
          h3: "text-sm font-medium text-text-primary mt-2 mb-1 first:mt-0",
          p: "text-sm text-text-medium mb-2 leading-relaxed",
          spacing: "space-y-1"
        };
      case 'card':
        return {
          h1: "text-xl font-semibold text-text-primary mt-6 mb-3 first:mt-0",
          h2: "text-lg font-semibold text-text-primary mt-4 mb-2 first:mt-0",
          h3: "text-base font-medium text-text-primary mt-3 mb-2 first:mt-0",
          p: "text-sm text-text-medium mb-3 leading-relaxed",
          spacing: "space-y-2"
        };
      case 'chat':
        return {
          h1: "text-base font-semibold text-text-primary mt-3 mb-2 first:mt-0",
          h2: "text-sm font-semibold text-text-primary mt-2 mb-1 first:mt-0",
          h3: "text-sm font-medium text-text-primary mt-2 mb-1 first:mt-0",
          p: "text-sm text-text-medium mb-2 leading-relaxed",
          spacing: "space-y-1"
        };
      default: // 'default'
        return {
          h1: "text-2xl font-semibold text-text-primary mt-8 mb-4 first:mt-0",
          h2: "text-xl font-semibold text-text-primary mt-6 mb-3 first:mt-0",
          h3: "text-lg font-medium text-text-primary mt-4 mb-2 first:mt-0",
          p: "text-base text-text-medium mb-4 leading-relaxed",
          spacing: "space-y-3"
        };
    }
  };

  const styles = getVariantStyles();

  // Helper function to extract text from React nodes  
  const getTextFromReactNode = (node: React.ReactNode): string => {
    if (typeof node === 'string') return node;
    if (typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(getTextFromReactNode).join(' ');
    if (React.isValidElement(node)) {
      return getTextFromReactNode(node.props.children);
    }
    return '';
  };

  // Helper function to escape pipe characters in table cell content
  const escapePipesInTableContent = (content: string): string => {
    // Replace pipe characters with HTML entity to prevent table column confusion
    return content.replace(/\|/g, '&#124;');
  };

  // Expandable List Component
  const ExpandableList: React.FC<{
    children: React.ReactNode;
    type: 'ul' | 'ol';
    className: string;
  }> = ({ children, type, className }) => {
    const childrenArray = React.Children.toArray(children);
    
    // Filter out text nodes (whitespace/newlines) and only count actual React elements
    const actualItems = childrenArray.filter(child => React.isValidElement(child));
    
    // Generate a stable ID based on the first few items' content
    const contentHash = actualItems.slice(0, 3)
      .map(child => getTextFromReactNode(child))
      .join('|')
      .substring(0, 50);
    const listId = `list-${contentHash.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
    const maxItems = 5;
    const hasMore = actualItems.length > maxItems;
    const isExpanded = expandedLists[listId] || false;
    
    // Use actual items for visibility logic
    const visibleItems = isExpanded ? actualItems : actualItems.slice(0, maxItems);
    
    // But render all children (including whitespace) to preserve formatting
    const allVisibleChildren = isExpanded ? childrenArray : (() => {
      let elementCount = 0;
      const result = [];
      for (const child of childrenArray) {
        if (React.isValidElement(child)) {
          if (elementCount < maxItems) {
            result.push(child);
            elementCount++;
          } else {
            break;
          }
        } else {
          // Include whitespace/text nodes that come before the maxItems cutoff
          if (elementCount < maxItems) {
            result.push(child);
          }
        }
      }
      return result;
    })();

    if (!hasMore) {
      // If 5 items or less, render normally
      return (
        <ListContext.Provider value={type}>
          {type === 'ul' ? (
            <ul className={className}>{children}</ul>
          ) : (
            <ol className={className}>{children}</ol>
          )}
        </ListContext.Provider>
      );
    }

    return (
      <ListContext.Provider value={type}>
        <div className="mb-3">
          {type === 'ul' ? (
            <ul className={className}>{allVisibleChildren}</ul>
          ) : (
            <ol className={className}>{allVisibleChildren}</ol>
          )}
        
        <button
          onClick={() => {
            toggleList(listId);
          }}
          className="flex items-center space-x-2 text-sm text-primary hover:text-primary-hover transition-colors font-medium mt-2"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              <span>Show Less ({maxItems} of {actualItems.length})</span>
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              <span>Show {actualItems.length - maxItems} More</span>
            </>
          )}
        </button>
        </div>
      </ListContext.Provider>
    );
  };

  // Enhanced Table Component that uses CollapsibleTable
  const EnhancedTable: React.FC<{
    children: React.ReactNode;
  }> = React.useCallback(({ children }) => {
    // Extract table data from children
    const extractTableData = (tableChildren: React.ReactNode) => {
      const childrenArray = React.Children.toArray(tableChildren);
      let headers: string[] = [];
      let rows: string[][] = [];
      
      childrenArray.forEach((child) => {
        if (React.isValidElement(child)) {
          if (child.type === 'thead') {
            // Extract headers from thead
            const theadChildren = React.Children.toArray(child.props.children);
            theadChildren.forEach((theadChild) => {
              if (React.isValidElement(theadChild) && theadChild.type === 'tr') {
                const headerCells = React.Children.toArray(theadChild.props.children);
                headers = headerCells.map((cell) => {
                  if (React.isValidElement(cell)) {
                    // Get text content from cell
                    const textContent = getTextFromReactNode(cell.props.children);
                    return escapePipesInTableContent(textContent.trim());
                  }
                  return '';
                });
              }
            });
          } else if (child.type === 'tbody') {
            // Extract rows from tbody
            const tbodyChildren = React.Children.toArray(child.props.children);
            tbodyChildren.forEach((tbodyChild) => {
              if (React.isValidElement(tbodyChild) && tbodyChild.type === 'tr') {
                const rowCells = React.Children.toArray(tbodyChild.props.children);
                const rowData = rowCells.map((cell) => {
                  if (React.isValidElement(cell)) {
                    // Get text content from cell
                    const textContent = getTextFromReactNode(cell.props.children);
                    return escapePipesInTableContent(textContent.trim());
                  }
                  return '';
                });
                rows.push(rowData);
              }
            });
          }
        }
      });
      
      return { headers, rows };
    };

    const { headers, rows } = extractTableData(children);
    
    // Simple approach: just use "Data Table" for all tables
    const tableTitle = 'Data Table';
    
    // Convert to CollapsibleTable format
    const tableData = {
      type: 'table' as const,
      title: tableTitle,
      headers: headers,
      data: rows.map(row => {
        const rowObj: Record<string, string> = {};
        headers.forEach((header, index) => {
          rowObj[header] = row[index] || '';
        });
        return rowObj;
      })
    };

    if (headers.length === 0 || rows.length === 0) {
      // Fallback to basic table rendering if data extraction fails
      return (
        <div className="overflow-x-auto mb-4">
          <table className="w-full border-collapse border border-border">
            {children}
          </table>
        </div>
      );
    }

    return (
      <div className="my-4">
        <CollapsibleTable 
          table={tableData}
          maxInitialRows={5}
          showExportButtons={true}
          showFullContent={true}
          images={images}
          hideTitle={true}
          isExporting={isExporting}
        />
      </div>
    );
  }, [images, isExporting]);

  // Container classes based on bordered prop
  const containerClasses = bordered 
    ? `bg-white rounded-lg border border-gray-200 p-6 shadow-sm ${className}`
    : className;

  // Pre-process content to handle pipe characters in table cells
  // First, handle the case where we have literal \n with <br /> tags
  let preprocessedContent = content;
  
  // Remove <br /> tags that appear before markdown elements
  preprocessedContent = preprocessedContent.replace(/<br\s*\/?>[\s]*\n/g, '\n');
  preprocessedContent = preprocessedContent.replace(/<br\s*\/?>/g, '\n');
  
  const processedContent = preprocessedContent.split('\n').map(line => {
    // Check if this line looks like a table row (starts with | and has multiple |)
    if (line.trim().startsWith('|') && (line.match(/\|/g) || []).length > 2) {
      // This is likely a table row, process each cell
      const cells = line.split('|');
      const processedCells = cells.map((cell, index) => {
        // Don't process the first and last elements (they're usually empty due to leading/trailing |)
        if (index === 0 || index === cells.length - 1) {
          return cell;
        }
        // For middle cells, replace any remaining pipes with HTML entity
        return cell.replace(/\|/g, '&#124;');
      });
      return processedCells.join('|');
    }
    return line;
  }).join('\n');

  return (
    <div className={containerClasses}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h1: (props) => (
            <>
              <h1 className={styles.h1}>{props.children}</h1>
              <hr className="border-gray-200 my-2" />
            </>
          ),
          h2: (props) => (
            <>
              <h2 className={styles.h2}>{props.children}</h2>
              <hr className="border-gray-200 my-2" />
            </>
          ),
          h3: (props) => (
            <>
              <h3 className={styles.h3}>{props.children}</h3>
              <hr className="border-gray-200 my-2" />
            </>
          ),
          h4: (props) => (
            <>
              <h4 className="text-sm font-medium text-text-primary mt-3 mb-1 first:mt-0">{props.children}</h4>
              <hr className="border-gray-200 my-2" />
            </>
          ),
          h5: (props) => (
            <>
              <h5 className="text-xs font-medium text-text-primary mt-2 mb-1 first:mt-0">{props.children}</h5>
              <hr className="border-gray-200 my-2" />
            </>
          ),
          h6: (props) => (
            <>
              <h6 className="text-xs font-medium text-text-secondary mt-2 mb-1 first:mt-0">{props.children}</h6>
              <hr className="border-gray-200 my-2" />
            </>
          ),
          p: (props) => <p className={styles.p}>{props.children}</p>,
          strong: (props) => <strong className="font-semibold text-text-primary">{props.children}</strong>,
          em: (props) => <em className="italic text-text-medium">{props.children}</em>,
          ul: (props) => (
            <ListContext.Provider value="ul">
              <ExpandableList type="ul" className={`list-none ${styles.spacing}`}>
                {props.children}
              </ExpandableList>
            </ListContext.Provider>
          ),
          ol: (props) => (
            <ListContext.Provider value="ol">
              <ExpandableList type="ol" className={`list-decimal list-inside ${styles.spacing}`}>
                {props.children}
              </ExpandableList>
            </ListContext.Provider>
          ),
          li: (props) => {
            // Get list type from context
            const listType = useContext(ListContext);
            
            if (listType === 'ol') {
              // For ordered lists, use default browser numbering
              return (
                <li className="text-text-medium ml-4">
                  {props.children}
                </li>
              );
            } else {
              // For unordered lists, use custom bullet
              return (
                <li className="text-text-medium flex items-start">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 mr-3 flex-shrink-0"></span>
                  <span>{props.children}</span>
                </li>
              );
            }
          },
          hr: () => <hr className="border-border my-4" />,
          blockquote: (props) => (
            <div className="relative my-4 max-w-full">
              {/* Quote icon positioned above the box */}
              <div className="absolute -top-3 left-8 z-10 bg-white px-2">
                <svg className="w-8 h-8 text-[#07E5D1]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
              </div>
              <div className="bg-[#E8FCFA] rounded-lg px-4 sm:px-8 py-6 border border-[#07E5D1]/20 overflow-hidden">
                <div className="text-[#374151] italic leading-relaxed text-sm text-left pl-2 sm:pl-4 pt-2 overflow-x-auto max-w-full break-words">
                  {props.children}
                </div>
              </div>
            </div>
          ),
          code: (props) => (
            <code className="bg-light-gray text-text-primary px-2 py-0.5 rounded text-sm font-mono break-words">
              {props.children}
            </code>
          ),
          pre: (props) => (
            <pre className="bg-light-gray p-4 rounded-lg overflow-x-auto overflow-y-auto max-h-96 mb-3 border border-border-light text-sm font-mono whitespace-pre-wrap break-words">
              {props.children}
            </pre>
          ),
          a: (props) => (
            <a 
              className="text-primary hover:text-primary-hover underline transition-colors"
              {...props}
            >
              {props.children}
            </a>
          ),
          table: (props) => (
            <EnhancedTable>
              {props.children}
            </EnhancedTable>
          ),
          th: (props) => (
            <th className="border border-border bg-light-gray px-3 py-2 text-left text-sm font-semibold text-text-primary">
              {props.children}
            </th>
          ),
          td: (props) => (
            <td className="border border-border px-3 py-2 text-sm text-text-medium">
              {props.children}
            </td>
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;